import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AppHeader,
  HeaderButton,
  HeaderIconLink,
  LanguageMenu,
  ThemeMenu,
  UserMenu,
  type NavItem,
} from '@bn-apps/dim-ui-react';
import { BaneNorLogo } from '@bn-apps/design-system-gfx';
import { Help, Logout } from '@bn-apps/design-system-icons';
import { useAuth } from '@hooks/useAuth';
import { useLocale } from '@hooks/useLocale';
import routes from '@config/routes';
import { isInSection, isOnPage, visibleNavigation } from '@config/navigation';
import QuickFind from './QuickFind';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'nb-NO', name: 'Norsk bokmål' },
  { code: 'nn-NO', name: 'Norsk nynorsk' },
];

const Header = (): JSX.Element => {
  const { t } = useTranslation();
  const { user, login, logout } = useAuth();
  const { language, setLanguage } = useLocale();
  const { pathname } = useLocation();
  const isAuthenticated = !!user?.isAuthenticated;

  // Home is reached through the logo. A menu item is highlighted on all pages of its
  // section, e.g. Data on /datasets. A section with one page is a plain link.
  const navigation: NavItem[] = (isAuthenticated ? visibleNavigation(!!user?.isProjectMember) : []).map((section) => {
    const pages = section.pages.map((page) => ({
      label: t(page.labelKey),
      href: page.path,
      description: t(page.summaryKey),
      isCurrent: isOnPage(pathname, page.path),
    }));
    return pages.length === 1
      ? pages[0]
      : { label: t(section.labelKey), isCurrent: isInSection(pathname, section), pages };
  });

  const isOnHelpPage = isOnPage(pathname, routes.help);

  return (
    <AppHeader
      appName={t('common.appTitle')}
      homeHref={routes.home}
      logo={<BaneNorLogo color="white" aria-hidden="true" />}
      navigation={navigation}
      navigationLabel={t('common.mainNavigation')}
      menuLabel={t('common.menu')}
      closeLabel={t('common.close')}
      currentSectionLabel={t('navigation.currentSection')}
      menuExtras={<HeaderIconLink href={routes.help} label={t('help.title')} icon={Help} isCurrent={isOnHelpPage} />}
      actions={
        <>
          <QuickFind isAuthenticated={isAuthenticated} isProjectMember={!!user?.isProjectMember} />
          {/* On phones Help is in the menu, so the icon makes room when there is a menu */}
          <HeaderIconLink
            href={routes.help}
            label={t('help.title')}
            icon={Help}
            isCurrent={isOnHelpPage}
            hideOnPhone={navigation.length > 0}
          />
          <ThemeMenu
            labels={{
              menu: t('common.theme'),
              light: t('common.light'),
              dark: t('common.dark'),
              system: t('common.system'),
            }}
          />
          <LanguageMenu
            label={t('common.language')}
            languages={languages}
            value={language}
            onChange={setLanguage}
          />
          {isAuthenticated ? (
            <UserMenu
              label={t('common.account')}
              name={user.name || user.email || ''}
              email={user.email ?? undefined}
              actions={[{ id: 'logout', label: t('auth.logout'), icon: Logout, onAction: logout }]}
            />
          ) : (
            <HeaderButton onPress={login}>{t('auth.login')}</HeaderButton>
          )}
        </>
      }
    />
  );
};

export default Header;
